import { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Camera, X, Loader2, Plus, Image as ImageIcon } from "lucide-react";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";

const ImageUpload = ({ 
  images = [], 
  onImagesChange, 
  maxImages = 5,
  folder = "stops",
  className = ""
}) => {
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    const remainingSlots = maxImages - images.length;
    const filesToUpload = files.slice(0, remainingSlots);

    if (files.length > remainingSlots) {
      toast.warning(`Only uploading ${remainingSlots} images (max ${maxImages})`);
    }

    setUploading(true);

    try {
      const uploadedUrls = [];

      for (const file of filesToUpload) {
        // Get signature from backend
        const sigResponse = await axios.get(`${API}/uploads/signature?folder=${folder}`);
        const sig = sigResponse.data;

        // Upload to Cloudinary
        const formData = new FormData();
        formData.append("file", file);
        formData.append("api_key", sig.api_key);
        formData.append("timestamp", sig.timestamp);
        formData.append("signature", sig.signature);
        formData.append("folder", sig.folder);

        const uploadResponse = await fetch(
          `https://api.cloudinary.com/v1_1/${sig.cloud_name}/image/upload`,
          { method: "POST", body: formData }
        );

        if (!uploadResponse.ok) {
          throw new Error("Upload failed");
        }

        const result = await uploadResponse.json();
        uploadedUrls.push(result.secure_url);
      }

      onImagesChange([...images, ...uploadedUrls]);
      toast.success(`${uploadedUrls.length} image(s) uploaded!`);
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("Failed to upload images");
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleRemoveImage = (indexToRemove) => {
    const updatedImages = images.filter((_, index) => index !== indexToRemove);
    onImagesChange(updatedImages);
  };

  return (
    <div className={className}>
      {/* Image Grid */}
      <div className="grid grid-cols-3 gap-2 mb-3">
        {images.map((url, index) => (
          <div key={index} className="relative aspect-square rounded-lg overflow-hidden bg-white/5">
            <img 
              src={url} 
              alt={`Upload ${index + 1}`}
              className="w-full h-full object-cover"
            />
            <button
              onClick={() => handleRemoveImage(index)}
              className="absolute top-1 right-1 w-6 h-6 bg-black/70 rounded-full flex items-center justify-center hover:bg-red-500 transition-colors"
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        ))}
        
        {/* Add Button */}
        {images.length < maxImages && (
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="aspect-square rounded-lg border-2 border-dashed border-white/20 hover:border-bronze/50 flex flex-col items-center justify-center transition-colors disabled:opacity-50"
          >
            {uploading ? (
              <Loader2 className="w-6 h-6 text-bronze animate-spin" />
            ) : (
              <>
                <Plus className="w-6 h-6 text-gray-500" />
                <span className="text-[10px] text-gray-500 mt-1">Add Photo</span>
              </>
            )}
          </button>
        )}
      </div>

      {/* Hidden File Input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={handleFileSelect}
        className="hidden"
      />

      {/* Upload Button for Empty State */}
      {images.length === 0 && !uploading && (
        <Button
          variant="outline"
          className="w-full h-10 border-white/20 hover:bg-white/5 text-sm"
          onClick={() => fileInputRef.current?.click()}
        >
          <Camera className="w-4 h-4 mr-2" />
          Add Photos
        </Button>
      )}

      <p className="text-[10px] text-gray-500 text-center mt-2">
        {images.length}/{maxImages} photos
      </p>
    </div>
  );
};

export default ImageUpload;
